import { buildWorkingSetGroups } from './relatedItemGroups';

const text = (value) => String(value ?? '').trim();

const positiveNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const vendorLabel = (option, item, vendorNameById) => (
  text(vendorNameById?.[option?.vendor_id])
  || text(option?.vendor_name_text)
  || text(item?.vendor)
  || 'Vendor not set'
);

const optionProduct = (option, item, vendorNameById) => ({
  id: option.id,
  vendor: vendorLabel(option, item, vendorNameById),
  name: text(option.vendor_description) || text(item.description) || 'Unnamed product',
  sku: text(option.vendor_sku),
  pack: text(option.pack_description) || text(item.pack_size),
  purchaseUnit: text(option.purchase_unit),
  packagePrice: positiveNumber(option.package_price),
  normalizedUnitCost: positiveNumber(option.normalized_unit_cost),
  // A vendor product recorded without any price: shown as "No price provided"
  // (never $0) and excluded from purchasing until a price arrives.
  priceMissing: positiveNumber(option.package_price) === null
    && positiveNumber(option.normalized_unit_cost) === null,
  isPreferred: option.is_preferred === true,
  conversionStatus: text(option.conversion_status),
  source: 'purchase_option',
});

const legacyProduct = (item) => ({
  id: `legacy-${item.id}`,
  vendor: text(item.vendor) || 'Vendor not set',
  name: text(item.description) || 'Unnamed product',
  sku: text(item.manufacturer_code),
  pack: text(item.pack_size),
  purchaseUnit: text(item.unit) || 'each',
  packagePrice: positiveNumber(item.case_price),
  normalizedUnitCost: positiveNumber(item.unit_price),
  priceMissing: false,
  isPreferred: true,
  conversionStatus: '',
  source: 'legacy',
});

// One vendor product renders ONCE. Same vendor entity (leading name token —
// naming drifts across "Sysco"/"Sysco Foods") + the EXACT same SKU is the
// same vendor product however many option rows carry it; the first (already
// preferred-sorted) row speaks for it. SKU-less products never collapse —
// without the vendor's identity key, two rows are two products. Counts,
// preferred choice and "N related products" all read the deduped list.
const vendorProductRenderKey = (product) => {
  if (!product.sku) return null;
  const vendorToken = product.vendor.trim().toLowerCase().split(/\s+/)[0] || '';
  return `${vendorToken}|${product.sku.toLowerCase()}`;
};

const dedupeVendorProducts = (products) => {
  const seen = new Set();
  return products.filter((product) => {
    const key = vendorProductRenderKey(product);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// What each relationship type MEANS to the person reading the strip. Only
// the three positive types render; a not_related row is a stored negative
// (it suppresses re-suggestion) and advertising it would just be noise.
export const RELATIONSHIP_TYPE_LABELS = {
  // A same_item row still has two different canonical ids (typically because
  // a grouping suggestion was declined). Never describe it as a vendor
  // option: vendor SKUs live on purchase_options, outside this graph.
  same_item: 'Equivalent — review item grouping',
  purchasing_alternative: 'Alternative — check differences',
  // Never the bare noun "related product": that is what the catalog calls a
  // vendor product sitting ON this item, and the two render inches apart.
  same_family: 'Same family — similar product',
};

/* ── Pair precedence (relationship domain model, 2026-08-18) ────────────────
 *
 * A confirmed EQUIVALENT closure owns its members: once two items are in one
 * confirmed same_item group, an alternative or same-family edge BETWEEN them
 * asserts nothing the group does not already say — and rendering it puts a
 * "check differences" card under items the fold just declared to be one
 * thing. The server rejects such rows on its next group pass, but the
 * display must not depend on drain timing, so every projection entry point
 * drops them here, over the SAME relationship rows the working set is built
 * from. Edges SPANNING two closures (Coke group ↔ Diet Coke group) are the
 * genuinely-broader case and always survive, as do same_item rows — they ARE
 * the closure.
 */
const dropPairsInsideEquivalentClosures = (relationships) => {
  const rows = Array.isArray(relationships) ? relationships : [];
  const { groupIdByItemId } = buildWorkingSetGroups(rows);
  if (!groupIdByItemId.size) return rows;
  return rows.filter((row) => {
    if (!row || row.relationship_type === 'same_item') return true;
    const left = groupIdByItemId.get(String(row.item_a_id));
    if (left === undefined) return true;
    return left !== groupIdByItemId.get(String(row.item_b_id));
  });
};

/**
 * item_relationships rows → a plain { itemId: rows[] } map keyed from BOTH
 * sides of each normalized pair, ready for
 * buildRelatedProductCatalogGroups' relationshipsByItemId param. Rejected
 * rows, not_related rows, junk, and non-equivalent pairs already inside one
 * confirmed EQUIVALENT closure are excluded here so every consumer shares
 * one visibility rule.
 */
export function groupRelationshipsByItemId(relationships) {
  const map = {};
  for (const row of dropPairsInsideEquivalentClosures(relationships)) {
    if (!row || row.id == null) continue;
    if (row.status === 'rejected') continue;
    if (!RELATIONSHIP_TYPE_LABELS[row.relationship_type]) continue;
    if (row.item_a_id === null || row.item_a_id === undefined
      || row.item_b_id === null || row.item_b_id === undefined
      || String(row.item_a_id) === String(row.item_b_id)) continue;
    if (row.item_a?.is_active === false || row.item_b?.is_active === false) continue;
    for (const itemId of [row.item_a_id, row.item_b_id]) {
      if (itemId === null || itemId === undefined) continue;
      const key = String(itemId);
      if (!map[key]) map[key] = [];
      map[key].push(row);
    }
  }
  return map;
}

const normalizedRelationshipKey = (row) => {
  if (!row || row.item_a_id === null || row.item_a_id === undefined
    || row.item_b_id === null || row.item_b_id === undefined) return null;
  const a = String(row.item_a_id);
  const b = String(row.item_b_id);
  if (a === b) return null;
  return a.localeCompare(b, undefined, { numeric: true }) < 0 ? `${a}|${b}` : `${b}|${a}`;
};

/** One display row per confirmed unordered canonical-item pair. */
export function confirmedRelationshipPairs(relationships) {
  const byPair = new Map();
  for (const row of dropPairsInsideEquivalentClosures(relationships)) {
    if (!row || row.status !== 'confirmed') continue;
    if (!RELATIONSHIP_TYPE_LABELS[row.relationship_type]) continue;
    if (row.item_a?.is_active === false || row.item_b?.is_active === false) continue;
    const key = normalizedRelationshipKey(row);
    if (!key || byPair.has(key)) continue;
    byPair.set(key, row);
  }
  return [...byPair.values()];
}

/** Symmetric rows touching one anchor item, separated by workflow state. */
export function relationshipRowsForItem(relationships, itemId, status) {
  const anchor = String(itemId ?? '');
  if (!anchor) return [];
  return dropPairsInsideEquivalentClosures(relationships).filter((row) => {
    if (!RELATIONSHIP_TYPE_LABELS[row?.relationship_type]) return false;
    if (status && row.status !== status) return false;
    if (row.item_a?.is_active === false || row.item_b?.is_active === false) return false;
    const key = normalizedRelationshipKey(row);
    if (!key) return false;
    return String(row.item_a_id) === anchor || String(row.item_b_id) === anchor;
  });
}

/**
 * Project pairwise relationship rows into scan-friendly connected groups.
 *
 * The database deliberately stores normalized A-B pairs. The catalog is more
 * useful when A-B plus B-C reads as one A/B/C relationship card, so this
 * helper builds connected components without changing the persistence model.
 * Members and groups are alphabetical to give every relationship workspace a
 * predictable default order.
 *
 * Components are built PER relationship_type, and that restriction is the
 * whole point. Chaining across types turned a store's catalog into category
 * blobs: "Beefsteak Tomatoes is an alternative to Roma" plus "Roma is the same
 * family as San Marzano #10 can" plus "Beefsteak is an alternative to Cherry"
 * collapsed into one seven-member card that also swept in Tomato Paste — a
 * grouping no single classification ever claimed and that fails the rule a
 * relationship is supposed to encode (these can do the same job in a recipe).
 * Keeping each type's graph separate means a card only ever asserts what its
 * rows actually say. Alternative is additionally pairwise and non-transitive:
 * A↔B plus B↔C produces two cards, never a three-product substitutability
 * claim. Its endpoints are first collapsed through their own Equivalent
 * closures so vendor members do not reappear as Alternative peers.
 *
 * Pair precedence applies BEFORE the per-type union-find: a non-same_item
 * row whose two endpoints already share one confirmed EQUIVALENT closure
 * (computed over the same `relationships` input, whatever `status` is being
 * projected) never becomes a card, so an "alternative" between two items the
 * catalog folds into one card cannot render anywhere.
 */
export function buildRelationshipGroups(relationships, items = [], status = 'confirmed', {
  optionsByItemId = {},
  storedCanonicalByGroupKey = {},
} = {}) {
  const itemById = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item?.id !== null && item?.id !== undefined) itemById.set(String(item.id), item);
  });

  const rows = dropPairsInsideEquivalentClosures(relationships).filter((row) => {
    if (!row || row.status !== status) return false;
    if (!RELATIONSHIP_TYPE_LABELS[row.relationship_type]) return false;
    if (row.item_a?.is_active === false || row.item_b?.is_active === false) return false;
    return normalizedRelationshipKey(row) !== null;
  });

  rows.forEach((row) => {
    if (row.item_a && !itemById.has(String(row.item_a_id))) itemById.set(String(row.item_a_id), row.item_a);
    if (row.item_b && !itemById.has(String(row.item_b_id))) itemById.set(String(row.item_b_id), row.item_b);
  });

  const equivalentGroups = buildWorkingSetGroups(relationships);
  const conceptFor = (itemId) => {
    const rawId = String(itemId);
    const groupId = equivalentGroups.groupIdByItemId.get(rawId);
    if (!groupId) return rawId;
    const memberIds = equivalentGroups.membersByGroupId.get(groupId) || [rawId];
    return resolveCanonicalMemberId(memberIds, {
      storedCanonicalId: storedCanonicalByGroupKey?.[String(groupId)] ?? null,
      optionsByItemId,
    }) || rawId;
  };
  const endpointHasEquivalentSiblings = (itemId) => {
    const groupId = equivalentGroups.groupIdByItemId.get(String(itemId));
    if (!groupId) return false;
    return (equivalentGroups.membersByGroupId.get(groupId) || []).length > 1;
  };

  const rowsByType = new Map();
  rows.forEach((row) => {
    const type = row.relationship_type;
    if (!rowsByType.has(type)) rowsByType.set(type, []);
    rowsByType.get(type).push(row);
  });

  const groups = [];
  rowsByType.forEach((typeRows, relationshipType) => {
    // Alternative is a direct, non-transitive relationship between PRODUCT
    // CONCEPTS. First collapse each endpoint through its Equivalent closure,
    // then emit one card per concept pair. Duplicate RD→Sysco and
    // Costco→Sysco rows remain stored but project as one Luxardo→Sysco card.
    if (relationshipType === 'purchasing_alternative') {
      const rowsByConceptPair = new Map();
      typeRows.forEach((row) => {
        const left = conceptFor(row.item_a_id);
        const right = conceptFor(row.item_b_id);
        if (left === right) return;
        const ids = [left, right].sort(idAscending);
        const key = `${ids[0]}|${ids[1]}`;
        if (!rowsByConceptPair.has(key)) rowsByConceptPair.set(key, { ids, rows: [] });
        rowsByConceptPair.get(key).rows.push(row);
      });
      rowsByConceptPair.forEach(({ ids, rows: pairRows }) => {
        const members = ids.map((id) => itemById.get(id) || { id, description: `Item ${id}` })
          .sort((left, right) => (
            (text(left?.description) || `Item ${left?.id}`).localeCompare(
              text(right?.description) || `Item ${right?.id}`,
              undefined,
              { sensitivity: 'base', numeric: true },
            )
          ));
        const conceptIdByRawItemId = {};
        pairRows.forEach((row) => {
          conceptIdByRawItemId[String(row.item_a_id)] = conceptFor(row.item_a_id);
          conceptIdByRawItemId[String(row.item_b_id)] = conceptFor(row.item_b_id);
        });
        const anchor = members[0];
        groups.push({
          id: `${status}:${relationshipType}:${ids.join('|')}`,
          status,
          title: text(anchor?.description) || `Item ${anchor?.id}`,
          anchorId: anchor?.id,
          members,
          memberIds: members.map((item) => item.id),
          rows: pairRows,
          relationshipType,
          conceptIdByRawItemId,
          // Use the concept renderer whenever either endpoint belongs to a
          // multi-member Equivalent closure, even if this particular
          // Alternative row happens to point at that closure's canonical
          // member. Otherwise a valid one-edge Luxardo→Sysco relationship
          // falls back to the old vendor-member presentation merely because
          // there is no duplicate Costco→Sysco row to reveal the collapse.
          hasCollapsedEquivalentConcepts: pairRows.some((row) => (
            endpointHasEquivalentSiblings(row.item_a_id)
            || endpointHasEquivalentSiblings(row.item_b_id)
          )),
        });
      });
      return;
    }

    const parent = new Map();
    const ensure = (id) => {
      const key = String(id);
      if (!parent.has(key)) parent.set(key, key);
      return key;
    };
    const find = (id) => {
      const key = ensure(id);
      const current = parent.get(key);
      if (current === key) return key;
      const root = find(current);
      parent.set(key, root);
      return root;
    };
    const union = (left, right) => {
      const leftRoot = find(left);
      const rightRoot = find(right);
      if (leftRoot !== rightRoot) parent.set(rightRoot, leftRoot);
    };

    typeRows.forEach((row) => union(row.item_a_id, row.item_b_id));

    const memberIdsByRoot = new Map();
    parent.forEach((_value, id) => {
      const root = find(id);
      if (!memberIdsByRoot.has(root)) memberIdsByRoot.set(root, new Set());
      memberIdsByRoot.get(root).add(id);
    });

    memberIdsByRoot.forEach((memberIds) => {
      const members = [...memberIds].map((id) => (
        itemById.get(id) || { id, description: `Item ${id}` }
      )).sort((left, right) => (
        (text(left?.description) || `Item ${left?.id}`).localeCompare(
          text(right?.description) || `Item ${right?.id}`,
          undefined,
          { sensitivity: 'base', numeric: true },
        )
      ));
      const idSet = new Set(members.map((item) => String(item.id)));
      const groupRows = typeRows.filter((row) => (
        idSet.has(String(row.item_a_id)) && idSet.has(String(row.item_b_id))
      ));
      const anchor = members[0];
      groups.push({
        // The type is part of the identity now: the same two items can hold
        // one card per type without the two cards colliding on one key.
        id: `${status}:${relationshipType}:${[...idSet].sort().join('|')}`,
        status,
        title: text(anchor?.description) || `Item ${anchor?.id}`,
        anchorId: anchor?.id,
        members,
        memberIds: members.map((item) => item.id),
        rows: groupRows,
        relationshipType,
      });
    });
  });

  return groups.sort((left, right) => (
    left.title.localeCompare(right.title, undefined, { sensitivity: 'base', numeric: true })
    || String(left.relationshipType).localeCompare(String(right.relationshipType))
  ));
}

/* ── Potential siblings of a Related Products group ──────────────────────────
 *
 * A group is "N ways to buy one product". The interesting question the moment
 * you look at one is: is there an N+1 the catalog already suspects?
 *
 * The evidence for that is sitting in item_relationships, but until now it was
 * only readable one screen away, in a workspace that talks about substitutes.
 * The Luxardo maraschino cherries were the case that made it plain: the group
 * card said "2 ways to buy it", and the fact that a third product had been
 * classified as related to it lived somewhere else entirely, in a card whose
 * two tiles were a collapsed concept and a single item — a shape nobody could
 * act on.
 *
 * A potential sibling is any live item that
 *   - is related to at least one member of this group, and
 *   - is not already a member.
 *
 * That covers both directions the question arrives from: a SUGGESTED row of
 * any type (the AI asking), and a CONFIRMED alternative or family row (a
 * decision already made that the user may want to promote — "these are not
 * substitutes, they are the same thing bought two ways").
 *
 * Confirmed same_item rows never appear, because both their endpoints are in
 * the group by construction.
 */

// Strength ordering when two members both point at the same outside item.
// A confirmed classification outranks a suggested one; within a status, the
// narrower claim outranks the broader.
const SIBLING_STATUS_RANK = { confirmed: 2, suggested: 1 };
const SIBLING_TYPE_RANK = { same_item: 3, purchasing_alternative: 2, same_family: 1 };

const siblingStrength = (row) => (
  (SIBLING_STATUS_RANK[row?.status] ?? 0) * 10 + (SIBLING_TYPE_RANK[row?.relationship_type] ?? 0)
);

/**
 * The items that could join one equivalent group, newest evidence first.
 *
 * @param {Array}  relationships  item_relationships rows
 * @param {Array}  memberItemIds  the group's current members
 * @returns {Array<{ id, otherItemId, relationshipType, status, confidence,
 *                   reasoning, otherItem }>} one entry per candidate item
 */
export function potentialSiblingsForGroup(relationships, memberItemIds) {
  const members = new Set((Array.isArray(memberItemIds) ? memberItemIds : [])
    .filter((id) => id !== null && id !== undefined).map(String));
  if (!members.size) return [];

  const byOtherItemId = new Map();
  (Array.isArray(relationships) ? relationships : []).forEach((row) => {
    if (!row || row.id === null || row.id === undefined) return;
    if (row.status !== 'suggested' && row.status !== 'confirmed') return;
    // not_related is a stored human "no", and an unknown type is junk.
    if (!RELATIONSHIP_TYPE_LABELS[row.relationship_type]) return;
    if (row.item_a_id === null || row.item_a_id === undefined
      || row.item_b_id === null || row.item_b_id === undefined) return;

    const a = String(row.item_a_id);
    const b = String(row.item_b_id);
    // Exactly one endpoint inside the group. Both inside means the row says
    // nothing the group does not already say; neither inside is not our row.
    const aIn = members.has(a);
    const bIn = members.has(b);
    if (aIn === bIn) return;

    const otherItemId = aIn ? b : a;
    const otherItem = aIn ? row.item_b : row.item_a;
    if (otherItem?.is_active === false) return;

    const entry = {
      id: row.id,
      otherItemId,
      otherItem: otherItem || null,
      relationshipType: row.relationship_type,
      status: row.status,
      confidence: row.confidence ?? null,
      reasoning: [row.reasoning, row.substitution_notes]
        .map((value) => text(value)).filter(Boolean).join(' ') || null,
    };
    const existing = byOtherItemId.get(otherItemId);
    if (!existing || siblingStrength(row) > siblingStrength({
      status: existing.status, relationship_type: existing.relationshipType,
    })) {
      byOtherItemId.set(otherItemId, entry);
    }
  });

  return [...byOtherItemId.values()].sort((left, right) => {
    const strength = siblingStrength({ status: right.status, relationship_type: right.relationshipType })
      - siblingStrength({ status: left.status, relationship_type: left.relationshipType });
    if (strength !== 0) return strength;
    return idAscending(left.otherItemId, right.otherItemId);
  });
}

/**
 * The relationship types the Substitutes & Alternatives workspace exists to
 * show. Confirmed `same_item` is deliberately absent: a confirmed equivalence
 * IS a Related Products group, RelatedProductGroupsView already folds and
 * renders it, and repeating it one click further in as "Equivalent — grouped
 * items" described the same rows twice.
 *
 * This is a PROJECTION rule, not a cleanup: the confirmed same_item rows stay
 * in the database, keep building the equivalent working set, keep deciding
 * canonical members, and keep suppressing re-suggestion. Only their second
 * rendering goes away.
 *
 * SUGGESTED same_item rows are a different thing entirely — an unanswered
 * question — and still belong in Review Suggestions.
 */
export const SECONDARY_WORKSPACE_TYPES = Object.freeze(['purchasing_alternative', 'same_family']);

export const isSecondaryWorkspaceType = (relationshipType) => (
  SECONDARY_WORKSPACE_TYPES.includes(relationshipType)
);

/**
 * Headline figures for the Substitutes & Alternatives workspace, computed
 * from exactly the rows that workspace renders — never from hidden database
 * rows. Confirmed equivalences are excluded (see SECONDARY_WORKSPACE_TYPES),
 * and each endpoint is counted as its EQUIVALENT GROUP where it has one, so a
 * Related Products group counts as the one product the card shows rather than
 * as each of its folded-away members.
 *
 * `suggestedRelationshipCount` still counts every unanswered pair of any
 * type, same_item included: a suggestion is a question, not a duplicate.
 */
export function secondaryRelationshipMetrics(relationships) {
  const confirmed = confirmedRelationshipPairs(relationships)
    .filter((row) => isSecondaryWorkspaceType(row?.relationship_type));
  const { groupIdByItemId } = buildWorkingSetGroups(relationships);
  const conceptOf = (itemId) => groupIdByItemId.get(String(itemId)) || String(itemId);
  const participatingItems = new Set();
  confirmed.forEach((row) => {
    participatingItems.add(conceptOf(row.item_a_id));
    participatingItems.add(conceptOf(row.item_b_id));
  });
  const suggested = new Set();
  dropPairsInsideEquivalentClosures(relationships).forEach((row) => {
    if (row?.status !== 'suggested' || !RELATIONSHIP_TYPE_LABELS[row.relationship_type]) return;
    const key = normalizedRelationshipKey(row);
    if (key) suggested.add(key);
  });
  return {
    confirmedRelationshipCount: confirmed.length,
    participatingItemCount: participatingItems.size,
    suggestedRelationshipCount: suggested.size,
  };
}

export function relationshipCatalogMetrics(relationships) {
  const confirmed = confirmedRelationshipPairs(relationships);
  const participatingItems = new Set();
  confirmed.forEach((row) => {
    participatingItems.add(String(row.item_a_id));
    participatingItems.add(String(row.item_b_id));
  });
  const suggested = new Set();
  // Same precedence rule as the cards, so the headline never counts a
  // suggestion no surface will render.
  dropPairsInsideEquivalentClosures(relationships).forEach((row) => {
    if (row?.status !== 'suggested' || !RELATIONSHIP_TYPE_LABELS[row.relationship_type]) return;
    const key = normalizedRelationshipKey(row);
    if (key) suggested.add(key);
  });
  return {
    confirmedRelationshipCount: confirmed.length,
    participatingItemCount: participatingItems.size,
    suggestedRelationshipCount: suggested.size,
  };
}

/**
 * The compact one-line rendering of material_differences:
 * '40% vs 36% milkfat · UHT vs fresh shelf treatment'. Entries without both
 * values fall back to the dimension name alone; empty in, '' out.
 */
export function materialDifferencesLabel(differences) {
  const parts = (Array.isArray(differences) ? differences : [])
    .map((diff) => {
      const a = text(diff?.source_value);
      const b = text(diff?.candidate_value);
      const dimension = text(diff?.dimension);
      if (a && b) return `${a} vs ${b}${dimension ? ` ${dimension}` : ''}`;
      return dimension || null;
    })
    .filter(Boolean);
  return parts.join(' · ');
}

// One cheap vendor/price line for the OTHER item of a relationship, from the
// props the catalog already holds: its preferred (or first) active option,
// else its legacy vendor/case price. Null when nothing is known.
const otherItemPriceSummary = (otherItemId, otherItem, optionsByItemId, vendorNameById) => {
  const options = (optionsByItemId?.[otherItemId] || [])
    .filter((option) => option && option.status !== 'inactive');
  const chosen = options.find((option) => option.is_preferred === true) || options[0] || null;
  if (chosen) {
    const vendor = vendorLabel(chosen, otherItem || {}, vendorNameById);
    const price = positiveNumber(chosen.package_price);
    return [vendor, price !== null ? `$${price.toFixed(2)}` : null].filter(Boolean).join(' · ') || null;
  }
  if (otherItem) {
    const vendor = text(otherItem.vendor);
    const price = positiveNumber(otherItem.case_price);
    return [vendor || null, price !== null ? `$${price.toFixed(2)}` : null].filter(Boolean).join(' · ') || null;
  }
  return null;
};

// One relationship row → the group-strip entry, seen FROM `item`'s side: the
// other item is named, its price summarized when the props already carry it,
// and the differences pre-rendered. Null for rows that should not render
// (unknown type, self-pairs, malformed).
const relationshipEntry = (row, item, itemById, optionsByItemId, vendorNameById) => {
  if (!row || row.id == null) return null;
  const typeLabel = RELATIONSHIP_TYPE_LABELS[row.relationship_type];
  if (!typeLabel) return null;
  const selfKey = String(item.id);
  const otherItemId = String(row.item_a_id) === selfKey ? row.item_b_id : row.item_a_id;
  if (otherItemId === null || otherItemId === undefined || String(otherItemId) === selfKey) return null;
  const embeddedOtherItem = String(row.item_a_id) === String(otherItemId) ? row.item_a : row.item_b;
  const otherItem = itemById.get(String(otherItemId)) || embeddedOtherItem || null;
  return {
    id: row.id,
    relationshipType: row.relationship_type,
    typeLabel,
    status: text(row.status) || 'suggested',
    confidence: positiveNumber(row.confidence),
    otherItemId,
    otherItem,
    otherItemName: text(otherItem?.description) || `Item ${otherItemId}`,
    priceSummary: otherItemPriceSummary(otherItemId, otherItem, optionsByItemId, vendorNameById),
    differencesLabel: materialDifferencesLabel(row.material_differences),
    comparisonPossible: text(row.comparison_possible) || 'unknown',
    substitutionNotes: text(row.substitution_notes) || null,
  };
};

/**
 * Project canonical catalog rows into their vendor-facing products. A linked
 * canonical item becomes one adjacent group of purchase options; an item with
 * zero or one active option remains in the same list as a standalone group.
 *
 * `relationshipsByItemId` ({ itemId: item_relationships rows[] } — see
 * groupRelationshipsByItemId) additionally gives each group its
 * cross-canonical `relationships` entries: OTHER items recorded as the same
 * item / a purchasing alternative / same family. Defaults to empty, so
 * existing callers are unchanged.
 */
export function buildRelatedProductCatalogGroups(
  items,
  optionsByItemId = {},
  vendorNameById = {},
  relationshipsByItemId = {},
) {
  const list = Array.isArray(items) ? items : [];
  const itemById = new Map(list.map((item) => [String(item?.id), item]));
  return list.map((item) => {
    const activeOptions = dedupeVendorProducts(
      (optionsByItemId?.[item.id] || [])
        .filter((option) => option && option.status !== 'inactive')
        .map((option) => optionProduct(option, item, vendorNameById))
        .sort((left, right) => {
          if (left.isPreferred !== right.isPreferred) return left.isPreferred ? -1 : 1;
          return left.vendor.localeCompare(right.vendor, undefined, { sensitivity: 'base' });
        }),
    );
    const products = activeOptions.length ? activeOptions : [legacyProduct(item)];
    const relationships = (relationshipsByItemId?.[item.id] || [])
      .map((row) => relationshipEntry(row, item, itemById, optionsByItemId, vendorNameById))
      .filter(Boolean);
    return {
      itemId: item.id,
      item,
      canonicalName: text(item.description) || `Item ${item.id}`,
      category: text(item.category),
      location: text(item.location),
      countUnit: text(item.unit) || 'each',
      products,
      relationships,
      isRelated: products.length > 1,
    };
  });
}

/* ── Equivalent-group folding (link-don't-merge) ─────────────────────────────
 *
 * Under link-don't-merge, "same product from another vendor" is a SEPARATE
 * inventory item joined by a confirmed same_item row (the EQUIVALENT working
 * set — relatedItemGroups.buildWorkingSetGroups). Each item then owns ONE
 * option, so `products.length > 1` stopped being how multi-vendor reality
 * looks: the helpers below fold a per-item projection down to one entry per
 * equivalent group so the catalog counts and renders groups, not fragments.
 */

// relatedItemGroups.byIdAscending's rule, restated: numeric when both ids are
// numeric, lexicographic otherwise.
const idAscending = (left, right) => {
  const l = Number(left);
  const r = Number(right);
  if (Number.isFinite(l) && Number.isFinite(r) && l !== r) return l - r;
  const ls = String(left);
  const rs = String(right);
  if (ls < rs) return -1;
  if (ls > rs) return 1;
  return 0;
};

/**
 * Which member represents an equivalent group in the catalog.
 *
 * Resolution order (link-don't-merge project, 2026-08-19):
 *   1. the STORED canonical (equivalent_group_comparisons.canonical_item_id)
 *      when it is one of the given members — the group layer's sticky
 *      election is authoritative once it has run;
 *   2. the member whose options carry the most recent
 *      last_operational_source_at — the member most recently touched by a
 *      real operational channel (invoice, import, edit);
 *   3. the lowest member id — deterministic, and the same convention
 *      buildWorkingSetGroups uses for the group id itself.
 *
 * @param {Array} memberIds  item ids (strings or numbers)
 * @param {object} [context] { storedCanonicalId, optionsByItemId }
 * @returns {string|null} the canonical member id as a string
 */
export function resolveCanonicalMemberId(memberIds, { storedCanonicalId = null, optionsByItemId = {} } = {}) {
  const members = (Array.isArray(memberIds) ? memberIds : [])
    .filter((id) => id !== null && id !== undefined)
    .map(String);
  if (!members.length) return null;

  if (storedCanonicalId !== null && storedCanonicalId !== undefined
    && members.includes(String(storedCanonicalId))) {
    return String(storedCanonicalId);
  }

  let recentMember = null;
  let recentTime = -Infinity;
  members.forEach((id) => {
    (optionsByItemId?.[id] || []).forEach((option) => {
      const parsed = Date.parse(option?.last_operational_source_at ?? '');
      if (Number.isFinite(parsed) && parsed > recentTime) {
        recentTime = parsed;
        recentMember = id;
      }
    });
  });
  if (recentMember !== null) return recentMember;

  return [...members].sort(idAscending)[0];
}

/**
 * Fold per-item catalog cards into ONE card per confirmed EQUIVALENT group.
 *
 * Cards whose item belongs to no working-set group pass through untouched.
 * A group's card wears the canonical member's identity (name, category,
 * count unit, `itemId` — so Edit and comparison lookups keep working) and
 * pools every member's vendor products: canonical's own first, then each
 * sibling's in ascending member-id order, deduped by option id and by the
 * (vendor, SKU) render key. Each pooled product records which member item it
 * belongs to (`memberItemId`, `memberName`), and — on multi-member cards —
 * the canonical member's FIRST product carries `isCanonical: true`, exactly
 * once per card, for the "Canonical" pill.
 *
 * Cross-canonical relationship entries are pooled the same way, minus the
 * entries that point INSIDE the group (the fold already says those items are
 * one thing).
 *
 * @param {Array}  groups      buildRelatedProductCatalogGroups output
 * @param {object} workingSet  buildWorkingSetGroups output (may be null)
 * @param {object} [context]   { storedCanonicalByGroupKey: {groupKey: itemId},
 *                               optionsByItemId } — canonical resolution inputs
 */
export function foldCatalogGroupsByEquivalence(groups, workingSet, {
  storedCanonicalByGroupKey = {},
  optionsByItemId = {},
} = {}) {
  const list = Array.isArray(groups) ? groups : [];
  if (!workingSet?.groupIdByItemId?.size) return list;

  const cardByItemId = new Map(list.map((card) => [String(card?.itemId), card]));
  const emittedGroupIds = new Set();
  const folded = [];

  list.forEach((card) => {
    const groupId = workingSet.groupIdByItemId.get(String(card?.itemId));
    if (!groupId) {
      folded.push(card);
      return;
    }
    if (emittedGroupIds.has(groupId)) return;
    emittedGroupIds.add(groupId);

    const memberIds = workingSet.membersByGroupId.get(groupId) || [String(card.itemId)];
    const presentIds = memberIds.map(String).filter((id) => cardByItemId.has(id));
    const missingMemberItemIds = memberIds.map(String).filter((id) => !cardByItemId.has(id));
    if (!presentIds.length) {
      folded.push(card);
      return;
    }

    const canonicalId = String(resolveCanonicalMemberId(presentIds, {
      storedCanonicalId: storedCanonicalByGroupKey?.[String(groupId)] ?? null,
      optionsByItemId,
    }));
    const canonicalCard = cardByItemId.get(canonicalId) || cardByItemId.get(presentIds[0]);
    const orderedIds = [canonicalId, ...presentIds.filter((id) => id !== canonicalId)];

    const seenProductIds = new Set();
    const seenRenderKeys = new Set();
    const products = [];
    // Exactly one pill per card: the canonical member's first product speaks
    // for the member's identity, however many products it owns.
    let canonicalMarked = false;
    orderedIds.forEach((memberId) => {
      const memberCard = cardByItemId.get(memberId);
      (memberCard?.products || []).forEach((product) => {
        const idKey = product?.id !== null && product?.id !== undefined ? String(product.id) : null;
        if (idKey && seenProductIds.has(idKey)) return;
        const renderKey = vendorProductRenderKey(product);
        if (renderKey && seenRenderKeys.has(renderKey)) return;
        if (idKey) seenProductIds.add(idKey);
        if (renderKey) seenRenderKeys.add(renderKey);
        const isCanonicalTile = memberId === canonicalId && !canonicalMarked && presentIds.length > 1;
        products.push({
          ...product,
          memberItemId: memberCard.itemId,
          memberName: memberCard.canonicalName,
          isCanonical: isCanonicalTile,
        });
        if (memberId === canonicalId) canonicalMarked = true;
      });
    });

    const memberIdSet = new Set(memberIds.map(String));
    const seenRelationshipIds = new Set();
    const relationships = [];
    orderedIds.forEach((memberId) => {
      (cardByItemId.get(memberId)?.relationships || []).forEach((entry) => {
        if (entry?.otherItemId !== null && entry?.otherItemId !== undefined
          && memberIdSet.has(String(entry.otherItemId))) return;
        const relKey = entry?.id !== null && entry?.id !== undefined ? String(entry.id) : null;
        if (relKey && seenRelationshipIds.has(relKey)) return;
        if (relKey) seenRelationshipIds.add(relKey);
        relationships.push(entry);
      });
    });

    folded.push({
      ...canonicalCard,
      products,
      relationships,
      // A group is a relationship even when every member holds ONE option:
      // membership itself is the second way to buy.
      isRelated: products.length > 1 || presentIds.length > 1,
      equivalentGroupId: String(groupId),
      equivalentMemberCount: memberIds.length,
      memberItemIds: presentIds,
      // A confirmed member may be beyond a paginated catalog load. Never
      // silently shrink the card: the host normally hydrates these endpoints,
      // and this explicit disposition remains if that fetch fails.
      missingMemberItemIds,
      canonicalItemId: canonicalId,
    });
  });

  return folded;
}

/**
 * Fold a FILTERED row list (the catalog finder's results) to one row per
 * equivalent group: the group's canonical member stands in for every matched
 * member. Filtering happens BEFORE this fold, so a hidden member that matches
 * the search still surfaces its group — through the canonical's row, which is
 * looked up in `rowById` (every loaded row, matched or not) and used even
 * when the canonical itself did not match. Rows outside any group pass
 * through; order is first-appearance.
 *
 * @param {Array}  rows      filtered rows ({id, ...}), display order
 * @param {object} workingSet buildWorkingSetGroups output (may be null)
 * @param {object} [context] { rowById: Map<string, row> of ALL loaded rows,
 *                             storedCanonicalByGroupKey, optionsByItemId }
 */
export function foldRowsToCanonicalMembers(rows, workingSet, {
  rowById = new Map(),
  storedCanonicalByGroupKey = {},
  optionsByItemId = {},
} = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!workingSet?.groupIdByItemId?.size) return list;

  const emittedGroupIds = new Set();
  const folded = [];
  list.forEach((row) => {
    if (row?.id === null || row?.id === undefined) {
      folded.push(row);
      return;
    }
    const groupId = workingSet.groupIdByItemId.get(String(row.id));
    if (!groupId) {
      folded.push(row);
      return;
    }
    if (emittedGroupIds.has(groupId)) return;
    emittedGroupIds.add(groupId);

    const memberIds = workingSet.membersByGroupId.get(groupId) || [String(row.id)];
    const loadedIds = memberIds.map(String).filter((id) => rowById.has(id));
    const canonicalId = resolveCanonicalMemberId(loadedIds.length ? loadedIds : [String(row.id)], {
      storedCanonicalId: storedCanonicalByGroupKey?.[String(groupId)] ?? null,
      optionsByItemId,
    });
    folded.push(rowById.get(String(canonicalId)) || row);
  });
  return folded;
}

/**
 * The groups the Related Products tab actually exists to show: a canonical
 * item beside the OTHER vendor products for the same item. An item with one
 * product is not a relationship — listing it next to itself is noise that
 * buried the real matches under hundreds of "Standalone" cards.
 */
export function relatedProductCatalogGroupsOnly(groups) {
  return (Array.isArray(groups) ? groups : []).filter((group) => group && group.isRelated);
}

/**
 * The counts behind the tab's summary line. An item earns a card two
 * different ways and the line must account for BOTH, or it describes a
 * different screen than the one under it: on the 2026-08-11 test catalog it
 * read "15 related groups · 31 vendor products · 8 single-product items
 * hidden" while 95 of 103 items were on screen — the 80 items shown purely
 * for their alternatives were counted by no number at all.
 *
 * `hiddenGroupCount` is the honest complement of what renders: items with
 * one way to buy AND no alternative. It is NOT `standaloneGroupCount`, which
 * counts every single-product item whether or not it is on screen.
 */
export function relatedProductCatalogMetrics(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const related = relatedProductCatalogGroupsOnly(list);
  const alternativeOnly = list.filter((group) =>
    group && !group.isRelated && (group.relationships?.length ?? 0) > 0);
  const shownGroupCount = related.length + alternativeOnly.length;
  return {
    inventoryItemCount: list.length,
    productCount: list.reduce((sum, group) => sum + (group?.products?.length || 0), 0),
    relatedGroupCount: related.length,
    relatedProductCount: related.reduce((sum, group) => sum + (group?.products?.length || 0), 0),
    standaloneGroupCount: list.length - related.length,
    // Shown for an alternative rather than for a second way to buy.
    alternativeOnlyGroupCount: alternativeOnly.length,
    shownGroupCount,
    hiddenGroupCount: list.length - shownGroupCount,
  };
}
